import { Row, Col, Modal, Form, Input, Tabs, Badge } from "antd";
import React, { useState, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import languages from "../../config/language";
import { MAKE_URL } from "../../constants/conts";
import SegmentSelect from "../../components/select/SegmentSelect";
import CategorySelect from "../../components/select/CategorySelect";
import FileUpload from "../../components/FileUpload";
const { TabPane } = Tabs;

const From = ({ path, sectionName, show, hide, data, refresh }) => {

  const [form] = Form.useForm();
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState([]);
  const [pdf, setPdf] = useState([]);
  const [validationStatus, setValidationStatus] = useState({ name: {}, display_name: {} });

  useEffect(() => {
    const initialNameStatus = {};
    const initialDisplayStatus = {};

    languages.forEach(lang => {
      initialNameStatus[lang.short] = data ? !!data[`${lang.short}_name`] : false;
      initialDisplayStatus[lang.short] = data ? !!data[`${lang.short}_display_name`] : false;
    });

    setValidationStatus({
      name: initialNameStatus,
      display_name: initialDisplayStatus,
    });

    if (data) {

      const { image_url, pdf_url, ...otherData } = data;
      const mappedImage = image_url ? [{ url: image_url }] : [];
      const mappedPdf = pdf_url ? [{ url: pdf_url }] : [];
      
      form.setFieldsValue({
        ...otherData,
        image: mappedImage,
        pdf: mappedPdf,
      });

      setImage(mappedImage);
      setPdf(mappedPdf);

    } else {
      form.resetFields();
    }

  }, [data, form]);

  const handleFieldChange = (value, lang, type) => {
    const fieldValue = form.getFieldValue(`${lang}_${type}`);

    setValidationStatus(prevStatus => ({
      ...prevStatus,
      [type]: {
        ...prevStatus[type],
        [lang]: !!fieldValue,
      }
    }));
  };

  const onCreate = async (values) => {

    const formData = new FormData();

    languages.forEach(lang => {
      formData.append(`${lang.short}_name`, values[`${lang.short}_name`] || "");
      formData.append(`${lang.short}_display_name`, values[`${lang.short}_display_name`] || "");
    });

    const segmentIds = values.segment_ids || [];
    const categoryIds = values.category_ids || [];
    
    formData.append("segment_ids", Array.isArray(segmentIds) ? segmentIds.join(',') : segmentIds);
    formData.append("category_ids", Array.isArray(categoryIds) ? categoryIds.join(',') : categoryIds);
    
    const imageData   =   values.image;
    const pdfData     =   values.pdf;
    
    if (imageData) {
      if (imageData.originFileObj) {
        formData.append("image", imageData.originFileObj);
      } else if (imageData.url) {
        const response = await fetch(imageData.url);
        const blob = await response.blob();
        const fileName = imageData.name || imageData.url.split("/").pop() || "file";
        formData.append("image", blob, fileName);
      }
    }

    if (pdfData) {
      if (pdfData.originFileObj) {
        formData.append("pdf", pdfData.originFileObj);
      } else if (pdfData.url) {
        const response = await fetch(pdfData.url);
        const blob = await response.blob();
        const fileName = pdfData.name || pdfData.url.split("/").pop() || "file";
        formData.append("pdf", blob, fileName);
      }
    }
    
    setLoading(true);
    request({
      url: MAKE_URL(path, data),
      method: 'POST',
      data: formData,
      onSuccess: (res) => {
        setLoading(false);
        if (res.status) {
          ShowToast(res.message, Severty.SUCCESS);
          hide();
          refresh();
        } else {
          ShowToast(res.message, Severty.ERROR);
        }
      },
      onError: (error) => {
        ShowToast(error.response.data.message, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  const handleCancel = () => {
    setValidationStatus({ name: {}, display_name: {} });
    hide();
  };

  const handleImage = (data) => {
    setImage(data);
    form.setFieldValue('image', data);
  }

  const handlePdf = (data) => {
    setPdf(data);
    form.setFieldValue('pdf', data);
  }

  return (
    <Modal
      open={show}
      title={`${data ? 'Update ' + sectionName : 'Create a New ' + sectionName}`}
      okText="Ok"
      onCancel={handleCancel}
      width={800}
      okButtonProps={{
        form: 'create',
        htmlType: 'submit',
        loading: loading,
      }}
    >
      <Form id="create" form={form} onFinish={onCreate} layout="vertical">

        <Tabs defaultActiveKey={languages[0].short} >
          {languages.map(lang => (
            <TabPane
              tab={
                <span>
                  {lang.long}
                  {(
                    validationStatus.name[lang.short] === false ||
                    validationStatus.display_name[lang.short] === false
                  ) && <Badge dot color="red" className="ml-1" />}
                </span>
              }
              key={lang.short}
            >

              <Row gutter={[16, 16]}>
                {/* Name */}
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label={`Name (${lang.long})`}
                    name={`${lang.short}_name`}
                    rules={[
                      ...(lang.short === 'en' ? [
                        { required: true, message: `Please enter the name in ${lang.long}!` }
                      ] : []),
                      { max: 100, message: "Name should not contain more than 100 characters!" },
                      { min: 2, message: "Name should contain at least 2 characters!" },
                    ]}
                    onChange={(e) => handleFieldChange(e.target.value, lang.short, 'name')}
                  >
                    <Input autoComplete="off" placeholder={`Enter name in ${lang.long}`} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label={`Display Name (${lang.long})`}
                    name={`${lang.short}_display_name`}
                    style={{ marginBottom: 16 }}
                    rules={[
                      ...(lang.short === 'en' ? [
                        { required: true, message: `Please enter the display name in ${lang.long}!` }
                      ] : []),
                      { max: 100, message: "Display name should not contain more than 100 characters!" },
                      { min: 2, message: "Display name should contain at least 2 characters!" },
                    ]}
                    onChange={(e) => handleFieldChange(e.target.value, lang.short, 'display_name')}
                  >
                    <Input autoComplete="off" placeholder={`Enter display name in ${lang.long}`} />
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>
          ))}
        </Tabs>
        
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={24} md={24}>
            <Form.Item
              label="Select Segments (Multiple)"
              name="segment_ids"
              required
              rules={[{ required: true, message: "Please select at least one segment!" }]}
            >
              <SegmentSelect
                multiple
                value={form.getFieldValue("segment_ids") || []}
                onChange={(val) => form.setFieldsValue({ segment_ids: val })}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={24}>
            <Form.Item
              label="Select Categories (Multiple)"
              name="category_ids"
              required
              rules={[{ required: true, message: "Please select at least one category!" }]}
            >
              <CategorySelect
                multiple
                value={form.getFieldValue("category_ids") || []}
                onChange={(val) => form.setFieldsValue({ category_ids: val })}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={12}>
            <Form.Item
              label="Upload Image"
              valuePropName="value"
              name="image"
            >
              <FileUpload filterType={["image"]} btnName={'Image'} onChange={(data) => handleImage(data)} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={12}>
            <Form.Item
              label="Upload PDF/Document"
              valuePropName="value"
              name="pdf"
            >
              <FileUpload filterType={["image", "file"]} btnName={'PDF/File'} onChange={(data) => handlePdf(data)} />
            </Form.Item>
          </Col>

        </Row>
      </Form>
    </Modal>
  );
};

export default From;
